import { useCallback, useState } from "react";
import { Typography } from "@mui/material";
import * as Auth from 'aws-amplify/auth';
import LoginHeader from "../Login/LoginHeader";
import { TextInput } from "../../components-redesign/TextInput/TextInput";
import PrimaryButton from "../../components-redesign/StyledButtons/PrimaryButton";
import { useLocalTranslationState } from "../../context/AppContext/AppContext";
import { styled } from "@mui/material/styles";
import { validateEmail } from "../../utilities/validations";

const FormContainer = styled("form")(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    minWidth: '100%',
  },
}));

const LoginBtnContainer = styled("div")(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-end',
  flexDirection: "column",
  paddingTop: theme.spacing(2.5),
}));

const TextContainer = styled("div")({
  alignSelf: "stretch",
});

const HeaderContainer = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(5),
}));

interface RegisterEmailProps {
  username;
  setUsername: (username) => void;
  setIsLoading: (loading) => void
  authError: string;
  setAuthError: (error) => void;
  setResetPasswordState: () => void;
}

const EnterEmail = ({
  username,
  setUsername,
  setIsLoading,
  setResetPasswordState,
  authError,
  setAuthError,
}: RegisterEmailProps) => {

  const { _translate } = useLocalTranslationState();

  const [, setResetPassword] = useState<any>();

  const isValid = () => {
    if (username) {
      if (validateEmail(username)) {
        setAuthError("")
        return true
      } else {
        setAuthError(_translate("Error, please correct this field"))
        return false
      }
    } else {
      setAuthError(_translate("Error, please complete this field"))
      return false
    }
  };

  const confirmReset = useCallback(async (email: string) => {
    const confirmOutput = await Auth.resetPassword({ username: email });
    return confirmOutput;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (isValid()) {
      try {
        const confirmOutput = await confirmReset(username)
        setResetPassword(confirmOutput);
        setResetPasswordState();
      } catch (error) {
        console.log("Caught aws-amplify/auth error:", error);
        setAuthError(error.message)
      }
    }
    setIsLoading(false);
  };

  return (
    <>
      <FormContainer noValidate onSubmit={handleSubmit}>
        <TextContainer>
          <Typography variant="subtitle2" fontWeight="400">
            {_translate("Step 1 of 2")}
          </Typography>
        </TextContainer>
        <HeaderContainer>
          <LoginHeader text={_translate("Forgotten password")} />
        </HeaderContainer>
        <TextInput
          fieldName="email"
          id="email"
          dataTestId="email"
          label={_translate("Email address")}
          value={username}
          isDisabled={false}
          isRequired={true}
          autoFocus={true}
          errorInPlainText={false}
          error={authError}
          onClear={() => setUsername('')}
          onChange={(event) => {
            const value = event.target.value;
            setUsername(() => value);
            if (value) {
              setAuthError('')
            }
          }}
        />

        <LoginBtnContainer>
          <PrimaryButton
            id="login-next-btn"
            text={_translate("Next")}
            dataTestId="next-btn"
          />
        </LoginBtnContainer>
      </FormContainer >
    </>
  );
};

export default EnterEmail;